import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  DataGrid,
  MuiEvent,
  GridColDef,
  GridRowModel,
  MuiBaseEvent,
  useGridApiRef,
  GridToolbarContainer,
  GridRowEditStopParams,
  GridRowEditStopReasons,
  GridColumnVisibilityModel,
  GridColumnResizeParams,
} from '@mui/x-data-grid'
import {
  Box,
  Select,
  Button,
  Checkbox,
  MenuItem,
  Popover,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import VisibilityIcon from '@mui/icons-material/Visibility'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { IPagination } from '../../../types/playbook.type'
import ConfirmationModal from '../../features/Modals/ConfirmationModal'
import { AppConfigContext } from '../../../contexts/app-config.context'
import { getTableCellColor } from '../../../utils/functions'

import styles from './styles.module.scss'

const LOCAL_STORAGE_KEY = 'table_column_config'

const colors: { [key: string]: { background: string; color: string } } = {}

interface IProps<T> {
  data: T[]
  configs: {
    title: string
    items: {
      key: string
      isTag: boolean
      column: string
      width?: number
      items?: any[]
      isDate?: boolean
      editable?: boolean
      defaultHidden?: boolean
    }[]
    columnWidth?: number
    isViewable: boolean
    isDeletable: boolean
    isEditable?: boolean
    isSearchable?: boolean
    isNominating?: boolean
    isSelectable?: boolean
  }
  freqStatusSelect?: boolean
  preface?: JSX.Element
  isLightMode: boolean
  addBtnText?: string
  hasWriteAccess?: boolean
  searchMaxWidth?: boolean
  isIncomplete?: boolean
  handleRefetch?: (pagination: IPagination, search?: string) => void
  importSfaf?: boolean
  handleView?: (item: any) => void
  handleEdit?: (item?: any) => void
  handleSave?: (row: any) => void
  handleCreate?: () => void
  handleDelete?: (id: number) => void
  handleSelect?: (item: any) => void
  handleNominate?: (item: any) => void
  handleOpenSfaf?: () => void
  setIsCompleting?: (item: any) => void
}

const TableComponent = <T extends Record<string, any>>({
  data,
  configs,
  preface,
  importSfaf,
  handleView,
  handleEdit,
  handleSave,
  handleCreate,
  handleSelect,
  handleDelete,
  isIncomplete,
  handleRefetch,
  handleNominate,
  handleOpenSfaf,
  setIsCompleting,
  freqStatusSelect,
  addBtnText,
  searchMaxWidth,
  hasWriteAccess,
}: IProps<T>) => {
  const [search, setSearch] = useState('')
  const [isDeleting, setIsDeleting] = useState(0)
  const [filteredData, setFilteredData] = useState<T[]>([])
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>({})
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({})
  const [columnOrder, setColumnOrder] = useState<string[]>([])
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false)
  const columnSettingsButtonRef = useRef<HTMLButtonElement | null>(null)
  const [settingsLoaded, setSettingsLoaded] = useState(false)

  const { isLightMode } = useContext(AppConfigContext)

  const apiRef = useGridApiRef()

  const storageKey = `${LOCAL_STORAGE_KEY}_${configs.title}`

  const saveSettingsToLocalStorage = () => {
    const settings = {
      columnVisibilityModel,
      columnWidths,
      columnOrder,
    }
    localStorage.setItem(storageKey, JSON.stringify(settings))
  }

  const handleColumnVisibilityChange = (newModel: GridColumnVisibilityModel) => {
    setColumnVisibilityModel(newModel)
  }

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    setColumnWidths((prevWidths) => ({
      ...prevWidths,
      [params.colDef.field]: params.width,
    }))
  }

  // Ordered list of config items for the column settings panel and getColumns
  const orderedConfigItems = useMemo(() => {
    if (columnOrder.length === 0) return configs.items
    return [...configs.items].sort((a, b) => {
      const aIdx = columnOrder.indexOf(a.key)
      const bIdx = columnOrder.indexOf(b.key)
      if (aIdx === -1 && bIdx === -1) return 0
      if (aIdx === -1) return 1
      if (bIdx === -1) return -1
      return aIdx - bIdx
    })
  }, [configs.items, columnOrder])

  const handleMoveColumn = (index: number, direction: 'up' | 'down') => {
    const newItems = [...orderedConfigItems]
    const swapIdx = direction === 'up' ? index - 1 : index + 1
    if (swapIdx < 0 || swapIdx >= newItems.length) return
    ;[newItems[index], newItems[swapIdx]] = [newItems[swapIdx], newItems[index]]
    setColumnOrder(newItems.map((item) => item.key))
  }

  const handleToggleVisibility = (key: string) => {
    const isVisible = columnVisibilityModel[key] !== false
    setColumnVisibilityModel((prev) => ({ ...prev, [key]: !isVisible }))
  }

  const handleSearch = (term: string) => {
    const searchResults = data.filter((elem) =>
      Object.values(elem).some((value) => String(value).toLocaleLowerCase().includes(term.toLowerCase())),
    )
    setSearch(term)
    setFilteredData(searchResults)
  }

  const handleViewClick = useCallback(
    (row: any) => {
      if (handleView) handleView(row)
    },
    [handleView],
  )

  const handleEditClick = useCallback(
    (row: any) => {
      if (setIsCompleting) setIsCompleting(row)
    },
    [setIsCompleting],
  )

  const handleRowEditStop = (params: GridRowEditStopParams, event: MuiEvent<MuiBaseEvent>) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true
    }
  }

  const processRowUpdate = useCallback(
    (newRow: GridRowModel) => {
      const updatedRows = filteredData.map((row) => (row.id === newRow.id ? { ...row, ...newRow } : row))
      setFilteredData(updatedRows)

      if (handleSave) {
        handleSave(newRow)
      }

      return newRow
    },
    [filteredData, handleSave],
  )

  const getColumns = useCallback(() => {
    const columns: GridColDef[] = orderedConfigItems.map((item) => ({
      field: item.key,
      headerName: item.column,
      sortable: true,
      editable: item.editable,
      headerAlign: item.isTag ? 'center' : 'left',
      width: columnWidths[item.key] || item.width || 170,
      renderCell: (params) => {
        let value = params.row[item.key]

        if (item.isTag && value) {
          const color = getTableCellColor(colors, value, isLightMode)
          if (!colors[value]) colors[value] = color

          return (
            <Box className={styles.tagWrapper}>
              <Tooltip title={value} arrow>
                <Box
                  className={styles.tag}
                  sx={{
                    backgroundColor: color.background,
                  }}
                >
                  <Typography sx={{ color: color.color }} className={styles.text}>
                    {value}
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
          )
        }

        value = params.value

        if (item.isDate) {
          value = value.format('YYYY-MM-DD')
        }

        return value
      },
    }))

    if (configs.isNominating && hasWriteAccess) {
      columns.unshift({
        field: 'nominate',
        headerName: '',
        disableColumnMenu: true,
        sortable: false,
        align: 'center',
        width: 70,
        renderCell: (params) => {
          return (
            <IconButton onClick={() => handleNominate && handleNominate(params.row)}>
              <AddIcon />
            </IconButton>
          )
        },
      })
    }

    if (configs.isViewable && hasWriteAccess) {
      columns.unshift({
        field: 'view',
        align: 'center',
        width: isIncomplete ? 110 : 70,
        disableColumnMenu: true,
        sortable: false,
        headerName: '',
        renderCell: (params) => {
          return (
            <>
              <IconButton onClick={() => handleViewClick(params.row)}>
                <VisibilityIcon />
              </IconButton>
              {isIncomplete && (
                <IconButton onClick={() => handleEditClick(params.row)}>
                  <EditIcon />
                </IconButton>
              )}
            </>
          )
        },
      })
    }

    if (configs.isEditable && handleEdit) {
      columns.push({
        field: 'edit',
        align: 'center',
        headerName: '',
        sortable: false,
        disableColumnMenu: true,
        width: 70,
        renderCell: (params) => {
          return (
            <IconButton onClick={() => handleEdit(params.row)}>
              <EditIcon />
            </IconButton>
          )
        },
      })
    }

    if (configs.isDeletable && handleDelete) {
      columns.push({
        field: 'delete',
        align: 'center',
        headerName: '',
        sortable: false,
        disableColumnMenu: true,
        width: 70,
        renderCell: (params) => {
          return (
            <IconButton onClick={() => setIsDeleting(params.row.id)}>
              <DeleteIcon color="error" />
            </IconButton>
          )
        },
      })
    }

    return columns
  }, [
    configs,
    handleNominate,
    handleView,
    handleEdit,
    handleDelete,
    setIsDeleting,
    isIncomplete,
    setIsCompleting,
    columnWidths,
    orderedConfigItems,
  ])

  const CustomToolbar = useCallback(() => {
    if (apiRef) {
      return (
        <GridToolbarContainer>
          <Tooltip title="Manage columns">
            <IconButton size="small" ref={columnSettingsButtonRef} onClick={() => setColumnSettingsOpen(true)}>
              <ViewColumnIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={() => apiRef.current && apiRef.current.exportDataAsCsv()}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </GridToolbarContainer>
      )
    } else {
      return null
    }
  }, [apiRef])

  useEffect(() => {
    if (settingsLoaded) saveSettingsToLocalStorage()
  }, [columnVisibilityModel, columnWidths, columnOrder, settingsLoaded])

  useEffect(() => {
    setFilteredData(data)
  }, [data])

  useEffect(() => {
    const savedSettings = localStorage.getItem(storageKey)

    if (savedSettings) {
      const { columnVisibilityModel, columnWidths, columnOrder } = JSON.parse(savedSettings)
      setColumnVisibilityModel(columnVisibilityModel || {})
      setColumnWidths(columnWidths || {})
      setColumnOrder(columnOrder || [])
    } else {
      const defaultVisibility: GridColumnVisibilityModel = {}
      configs.items.forEach((item) => {
        if (item.defaultHidden) defaultVisibility[item.key] = false
      })
      if (Object.keys(defaultVisibility).length > 0) {
        setColumnVisibilityModel(defaultVisibility)
      }
    }
    setSettingsLoaded(true)
  }, [])

  return (
    <Box className={styles.tableComponent}>
      <Box className={styles.titleContainer}>
        {handleCreate && hasWriteAccess && (
          <Button variant="outlined" onClick={handleCreate}>
            {addBtnText ?? 'Create'}
          </Button>
        )}
        {hasWriteAccess && preface}
      </Box>
      {configs.isSearchable && (
        <Box className={styles.searchContainer}>
          <TextField
            className={styles.search}
            sx={{ maxWidth: importSfaf || freqStatusSelect || searchMaxWidth ? 'auto' : 300 }}
            value={search}
            label="Search"
            onChange={(evt) => handleSearch(evt.target.value)}
          />
          {importSfaf && (
            <Button className={styles.importBtn} variant="outlined" onClick={handleOpenSfaf}>
              Import
            </Button>
          )}
          {freqStatusSelect && (
            <Select
              defaultValue={'NOT_STARTED'}
              className={styles.select}
              onChange={(evt) => handleRefetch && handleRefetch({} as IPagination, String(evt.target.value))}
            >
              {['NOT_STARTED', 'ACCEPTED', 'APPROVED', 'DENIED'].map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          )}
        </Box>
      )}
      <Box className={styles.tableWrapper}>
        <Box className={styles.table}>
          <DataGrid
            className={styles.dataGrid}
            getRowId={(row) => row.tableId}
            rows={filteredData.map((elem, ind) => ({
              ...elem,
              tableId: configs.title === 'Requests' ? ind : elem.id,
            }))}
            apiRef={apiRef}
            columns={getColumns()}
            onColumnWidthChange={handleColumnWidthChange}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleColumnVisibilityChange}
            slots={{
              footer: CustomToolbar,
            }}
            processRowUpdate={processRowUpdate}
            slotProps={{
              baseButton: { sx: { mb: 1, border: '1px solid white' } },
            }}
            onRowEditStop={handleRowEditStop}
            rowHeight={35}
            columnHeaderHeight={35}
            onRowClick={(params) => handleSelect && handleSelect(params.row)}
            sx={{
              '& .MuiDataGrid-row.Mui-selected': {
                background: 'rgb(183 181 181 / 30%)',
              },
              '& .MuiDataGrid-row.Mui-selected:hover': {
                background: 'rgb(183 181 181 / 30%)',
              },
            }}
          />
        </Box>
      </Box>
      <Popover
        open={columnSettingsOpen}
        anchorEl={columnSettingsButtonRef.current}
        onClose={() => setColumnSettingsOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1, minWidth: 260, maxHeight: 400, overflowY: 'auto' }}>
          <Typography variant="caption" sx={{ px: 1, opacity: 0.6 }}>
            Show / Hide · Order of Rows
          </Typography>
          <List dense disablePadding>
            {orderedConfigItems.map((item, index) => {
              const isVisible = columnVisibilityModel[item.key] !== false
              return (
                <ListItem
                  key={item.key}
                  disableGutters
                  sx={{ py: 0 }}
                  secondaryAction={
                    <Box sx={{ display: 'flex' }}>
                      <IconButton size="small" disabled={index === 0} onClick={() => handleMoveColumn(index, 'up')}>
                        <ArrowUpwardIcon fontSize="inherit" />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={index === orderedConfigItems.length - 1}
                        onClick={() => handleMoveColumn(index, 'down')}
                      >
                        <ArrowDownwardIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  }
                >
                  <Checkbox
                    size="small"
                    checked={isVisible}
                    onChange={() => handleToggleVisibility(item.key)}
                    sx={{ py: 0 }}
                  />
                  <ListItemText primary={item.column} />
                </ListItem>
              )
            })}
          </List>
        </Box>
      </Popover>
      {!!isDeleting && (
        <ConfirmationModal
          text="Are you sure you want to delete this row"
          btnText="Delete"
          btnIcon={<DeleteIcon />}
          onConfirm={() => {
            setIsDeleting(0)
            handleDelete && handleDelete(isDeleting)
          }}
          onClose={() => setIsDeleting(0)}
        />
      )}
    </Box>
  )
}

export default TableComponent
